import { CommonModule } from '@angular/common';
import { Component, OnInit, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import {
  AlertController,
  IonicModule,
  LoadingController,
  ModalController,
  NavController,
  PopoverController,
} from '@ionic/angular';
import _ from 'lodash';
import { NgxTippyModule } from 'ngx-tippy-wrapper';
import { firstValueFrom } from 'rxjs';

import {
  ActionDropdownComponent,
  ActionDropdownType,
} from 'src/app/components/action-dropdown/action-dropdown.component';
import { ChannelSelectorModalComponent } from 'src/app/components/channel-selector/channel-selector-modal/channel-selector-modal.component';
import { ChannelSelectorModule } from 'src/app/components/channel-selector/channel-selector.module';
import { EmojiPickerComponent } from 'src/app/components/emoji-picker/emoji-picker.component';
import { ImageModalComponent } from 'src/app/components/image-modal/image-modal.component';
import { SupportStepsModule } from 'src/app/components/support-steps/support-steps.module';
import { WorkingHoursComponent } from 'src/app/components/working-hours/working-hours.component';
import {
  CONVERSATION_PRIORITY_VALUES,
  DEFAULT_PRIORITY_LABEL_MAP_FORM,
  DEFAULT_PRIORITY_TOKENS,
  FORM_PLACEHOLDER_DESCRIPTIONS,
  INBOX_TYPES,
  ProjectSlackInboxConfig,
  routes,
  STATUS_COLUMN_FORMAT_MAP,
  TenantSlackInboxConfig,
  WorkingHours,
} from 'src/app/constants';
import {
  fileNameFromImageModalUpload,
  fillPriorityLabelMapFormWithCurrentValues,
  genericCompareFn,
  getIconForConversationPriority,
  isUrl,
  processPriorityLabelMap,
} from 'src/app/utils/utils';
import { PriorityTokens, SlackChannel, SubStatus, TicketType } from 'src/models';
import {
  Project,
  PROJECT_PERMISSION_LEVELS,
  ProjectEscalationSettings,
  ProjectLevelPermissions,
  ProjectPermissionLevel,
  ProjectVisibility,
} from 'src/models/projects';
import { ApiService } from 'src/services/api.service';
import { AuthService } from 'src/services/auth.service';
import { EmojisService } from 'src/services/emojis.service';
import { MessageService } from 'src/services/message.service';
import { ProjectsService } from 'src/services/projects.service';
import { SearchablePopoverService } from 'src/services/searchable-popover.service';
import { SlackService } from 'src/services/slack.service';
import { TicketTypesService } from 'src/services/ticket-types.service';
import { UploadService } from 'src/services/upload.service';

import { ColonToEmojiPipe } from '../../../pipes/colonToEmoji.pipe';
import { CustomFieldsModule } from '../../components/custom-fields/custom-fields.module';

type SubStatusWithIcon = SubStatus & { icon?: string; iconClass?: string };

@Component({
  standalone: true,
  selector: 'app-edit-project',
  imports: [
    CommonModule,
    IonicModule,
    FormsModule,
    ChannelSelectorModule,
    CustomFieldsModule,
    NgxTippyModule,
    SupportStepsModule,
    ColonToEmojiPipe,
  ],
  templateUrl: './edit-project.component.html',
  styleUrls: ['./edit-project.component.scss'],
})
export class EditProjectComponent implements OnInit {
  @ViewChild('nameInput', { static: false }) nameInput;

  project: Partial<Project>;
  isLoading = false;
  isInitialized = false;
  name: string;
  originalName: string;
  description: string;
  id?: string;
  visibility: ProjectVisibility;
  isExternal: boolean;
  defaultTriageChannelId: string | null = null;
  defaultTriageChannel: SlackChannel | null = null;
  supportName: string | null = null;
  supportLogo: string | null = null;
  workingHours: WorkingHours | null = null;
  workingHoursEnabled: boolean = false;
  supportSteps: any[] = [];
  defaultTicketType: TicketType | null = null;
  instructions: string | null = null;
  triageMessageTemplateId: string | null = null;
  triageMessageTemplate: any | null = null;
  tenantSlackInboxConfig: TenantSlackInboxConfig | null = null;
  slackInboxConfig: ProjectSlackInboxConfig | null = {
    hideProjectInDirectTicketCreation: null,
  };

  shouldShowProjectLevelSlackInboxConfig = false;
  escalationSettings: ProjectEscalationSettings = {
    sendUserFacingNotifications: false,
  };
  showEscalationSettings = false;
  customTokens: Record<string, any> = {};
  priorityLabelMapForm: Record<string, PriorityTokens> = _.cloneDeep(DEFAULT_PRIORITY_LABEL_MAP_FORM);
  defaultPriorityLabelMap = DEFAULT_PRIORITY_TOKENS;
  FORM_PLACEHOLDER_DESCRIPTIONS = FORM_PLACEHOLDER_DESCRIPTIONS;
  public isURL = isUrl;
  approvePrefillAfterStatusId: string | null = null;

  availableSubStatuses: SubStatusWithIcon[] = [];
  selectedSubStatus: SubStatusWithIcon | null = null;
  approvePrefillAfterStatus: SubStatusWithIcon | null = null;
  STATUS_COLUMN_FORMAT_MAP = STATUS_COLUMN_FORMAT_MAP;

  emojiActionsConfig: Record<string, string[]> = {};
  projectLevelPermissions: ProjectLevelPermissions = {};
  PROJECT_PERMISSION_LEVELS = PROJECT_PERMISSION_LEVELS;

  // Permission level options for pill tabs
  readonly PERMISSION_LEVEL_OPTIONS = [
    { value: null, label: 'Agent Only', isFirst: true },
    {
      value: PROJECT_PERMISSION_LEVELS.AGENT_AND_SUBMITTER,
      label: 'Agents & Submitters',
    },
    { value: PROJECT_PERMISSION_LEVELS.ANYONE, label: 'Anyone', isLast: true },
  ] as const;

  // Track which permission level tab is selected for each action
  selectedPermissionTabs: Record<string, ProjectPermissionLevel | null> = {
    createConversation: null,
    closeConversation: null,
    inProgressConversation: null,
    onHoldConversation: null,
  };

  constructor(
    private route: ActivatedRoute,
    public authService: AuthService,
    private modalCtrl: ModalController,
    private alertCtrl: AlertController,
    private msgSrvc: MessageService,
    private projectsService: ProjectsService,
    private slackService: SlackService,
    private uploadService: UploadService,
    private navCtrl: NavController,
    private ticketTypesService: TicketTypesService,
    private searchablePopoverService: SearchablePopoverService,
    private apiService: ApiService,
    private emojisService: EmojisService,
    private popoverCtrl: PopoverController,
    private loadingCtrl: LoadingController,
  ) {
    this.id = this.route.snapshot.params['projectId'];
  }

  async ngOnInit(): Promise<void> {
    if (this.id) {
      await this.loadProject();
      this.loadSubStatuses();
    } else {
      this.visibility = 'public';
      this.isExternal = false;
      this.isInitialized = true;
      setTimeout(() => this.nameInput.setFocus(), 500);
    }
    await this.emojisService.loadEmojis();
  }

  async loadProject() {
    try {
      this.project = await this.projectsService.getById(this.id);
      const tenant = this.authService.tenant;
      this.tenantSlackInboxConfig = {
        ...tenant.slackInboxConfig,
        defaultHideProjectsInDirectTicketCreation:
          tenant.slackInboxConfig?.defaultHideProjectsInDirectTicketCreation ?? null,
      };

      this.name = this.project?.name;
      this.originalName = this.project?.name;
      this.description = this.project.description;
      this.visibility = this.project.visibility;
      this.isExternal = this.project.isExternal;
      this.defaultTriageChannelId = this.project.defaultTriageChannelId;
      this.triageMessageTemplateId = this.project.triageMessageTemplateId;
      this.supportName = this.project.supportName;
      this.supportLogo = this.project.supportLogo;
      this.workingHours = this.project.workingHours;
      this.workingHoursEnabled = this.project.workingHours && this.project.workingHours.enabled;
      this.supportSteps = this.project.supportSteps;
      this.instructions = this.project.instructions;
      this.shouldShowProjectLevelSlackInboxConfig = !tenant.slackInboxConfig.disableDirectTicketCreation;
      this.slackInboxConfig = {
        hideProjectInDirectTicketCreation: this.project.slackInboxConfig?.hideProjectInDirectTicketCreation ?? null,
      };
      this.escalationSettings = this.project.escalationSettings;
      this.showEscalationSettings = tenant.featureFlags?.escalationsEnabled;
      this.customTokens = _.cloneDeep(this.project.customTokens || {});

      const currentPriorityMap = _.get(this.customTokens, 'PRIORITY_LABEL_MAP', {}) as Record<
        string,
        Partial<PriorityTokens> | null
      >;
      this.priorityLabelMapForm = fillPriorityLabelMapFormWithCurrentValues(currentPriorityMap);
      // Load project level permissions
      this.projectLevelPermissions = this.project.projectLevelPermissions || {};

      // Initialize selected permission tabs based on loaded data
      this.selectedPermissionTabs.createConversation =
        this.projectLevelPermissions.permissionLevelForEmojiConversationCreation ?? null;
      this.selectedPermissionTabs.closeConversation =
        this.projectLevelPermissions.permissionLevelForEmojiConversationClosing ?? null;
      this.selectedPermissionTabs.inProgressConversation =
        this.projectLevelPermissions.permissionLevelForEmojiConversationInProgress ?? null;
      this.selectedPermissionTabs.onHoldConversation =
        this.projectLevelPermissions.permissionLevelForEmojiConversationOnHold ?? null;
      this.emojiActionsConfig = Object.keys(this.project.emojiActionsConfig).reduce((acc, emoji) => {
        const action = this.project.emojiActionsConfig[emoji];

        if (!acc[action]) {
          acc[action] = [];
        }

        acc[action].push(emoji);

        return acc;
      }, {});
      this.approvePrefillAfterStatusId = this.project.approvePrefillAfterStatusId;
      this.approvePrefillAfterStatus = this.project.approvePrefillAfterStatus;

      // After loading the project data, initialize the selected substatus display
      if (this.availableSubStatuses.length > 0) {
        this.updateSelectedSubStatus();
      }

      if (this.project?.defaultTicketTypeId) {
        this.loadDefaultTicketType(this.project.defaultTicketTypeId);
      }

      if (this.project?.defaultTriageChannelId) {
        this.loadTriageChannel();
      }

      if (this.project?.triageMessageTemplateId) {
        this.loadTriageMessageTemplate();
      }
      this.isInitialized = true;
    } catch (error) {
      await this.msgSrvc.showError(error);
    }
  }

  async loadSubStatuses() {
    if (!this.id) {
      return;
    }

    try {
      const result = await firstValueFrom(
        this.apiService.post<{ data: SubStatus[] }>('/sub-statuses/list', {
          where: [{ field: 'projectId', operator: '==', value: this.id }],
          order: [
            ['typeOrd', 'ASC'],
            ['position', 'ASC'],
          ],
          limit: 1000,
        }),
      );

      this.availableSubStatuses = (result.data || []).map((subStatus) => {
        const type = subStatus.key?.split('__')[0] || subStatus.type;
        const format = STATUS_COLUMN_FORMAT_MAP[type];
        return {
          ...subStatus,
          icon: format?.icon,
          iconClass: format?.iconClass,
        };
      });

      this.updateSelectedSubStatus();
    } catch (error) {
      console.error('Error loading sub-statuses:', error);
      this.msgSrvc.showError('Failed to load status options.');
      this.availableSubStatuses = [];
    }
  }

  updateSelectedSubStatus() {
    if (this.approvePrefillAfterStatusId || (this.approvePrefillAfterStatus && this.approvePrefillAfterStatus.id)) {
      const statusId = this.approvePrefillAfterStatusId || this.approvePrefillAfterStatus?.id;
      this.selectedSubStatus = this.availableSubStatuses.find((s) => s.id === statusId) || null;

      if (this.selectedSubStatus && !this.approvePrefillAfterStatusId) {
        this.approvePrefillAfterStatusId = this.selectedSubStatus.id;
      }
    } else {
      this.selectedSubStatus = null;
    }
  }

  async showSubStatusPopover(event: Event) {
    this.searchablePopoverService.subStatuses({
      event,
      selectedSubStatusId: this.approvePrefillAfterStatusId,
      showNoneOption: true,
      projectId: this.id,
      callback: (id, _name, subStatus) => {
        this.approvePrefillAfterStatusId = id;
        if (id && subStatus) {
          // Get the matching status from our processed array which has icon and iconClass
          if (subStatus.id) {
            this.selectedSubStatus = this.availableSubStatuses.find((s) => s.id === subStatus.id) || null;
          } else {
            // Handle the case where "None" is selected
            this.selectedSubStatus = null;
          }
        } else {
          this.selectedSubStatus = null;
        }
      },
    });
  }

  getStatusTypeLabel(statusType: string): string {
    const labels = {
      new: 'New',
      open: 'Open',
      in_progress: 'In Progress',
      on_hold: 'On Hold',
      closed: 'Closed',
    };
    return labels[statusType] || statusType;
  }

  async loadDefaultTicketType(id: string) {
    const ticketTypesResponse = await this.ticketTypesService.list({
      limit: null,
      order: ['name'],
      descending: false,
      where: [
        {
          field: 'id',
          operator: '==',
          value: id,
        },
      ],
    });

    this.defaultTicketType = ticketTypesResponse.data?.[0] as TicketType;
  }

  async loadTriageChannel() {
    try {
      const triageChannel = await this.slackService.getChannel(this.defaultTriageChannelId);

      if (triageChannel) {
        this.defaultTriageChannel = triageChannel;
      }
    } catch (e) {
      this.msgSrvc.showError(e);
    }
  }

  async loadTriageMessageTemplate() {
    try {
      const template = await this.apiService.getPromise(`/templates/${this.triageMessageTemplateId}`);

      this.triageMessageTemplate = template;
    } catch (e) {
      this.msgSrvc.showError(e);
    }
  }

  async setTriageMessageTemplate() {
    this.searchablePopoverService.templates({
      event,
      showNoneOption: true,
      selectedTemplateId: this.triageMessageTemplateId,
      projectId: this.id,
      callback: (id: string, _title: string, data: any) => {
        if (this.triageMessageTemplateId === id) {
          this.triageMessageTemplateId = null;
          this.triageMessageTemplate = null;
        } else {
          this.triageMessageTemplateId = id;
          this.triageMessageTemplate = data;
        }
      },
    });
  }

  channelDidChange(slackChannel: SlackChannel) {
    this.defaultTriageChannel = slackChannel;
    this.defaultTriageChannelId = slackChannel?.id ?? null;
  }

  async setDefaultTriageChannel() {
    const modal = await this.modalCtrl.create({
      component: ChannelSelectorModalComponent,
      componentProps: {
        selectedChannel: this.defaultTriageChannel,
        defaultInternalOnly: true,
        defaultExternalOnly: false,
        hideChannelTypeFilter: true,
        hideSecondaryTeams: false,
      },
    });

    modal.present();

    modal.onDidDismiss().then(({ data }: any) => {
      if (data?.channel) {
        this.defaultTriageChannel = data.channel;
        this.defaultTriageChannelId = data.channel.id;
      }
    });
  }

  genericCompareFn(v1, v2) {
    return genericCompareFn(v1, v2);
  }

  compareWith(o1: any, o2: any) {
    if (o1 === undefined || o1 === null || o2 === undefined || o2 === null) {
      return o1 === o2;
    }
    return String(o1) === String(o2);
  }

  setDefaultTicketType() {
    this.searchablePopoverService.ticketTypes({
      event,
      showNoneOption: false,
      projectId: this.id,
      callback: (id: string, _title: string, data: TicketType) => {
        if (this.defaultTicketType?.id === id) {
          this.defaultTicketType = null;
        } else {
          this.defaultTicketType = data ?? undefined;
        }
      },
    });
  }

  async submit() {
    if (!this.name) {
      await this.msgSrvc.show('Whoops! Please enter a name.');

      return;
    }

    this.isLoading = true;
    if (!this.customTokens) {
      this.customTokens = {};
    }

    this.customTokens.PRIORITY_LABEL_MAP = processPriorityLabelMap(this.priorityLabelMapForm);

    const updates = {
      name: this.name,
      description: this.description,
      visibility: this.visibility,
      isExternal: this.isExternal,
      defaultTriageChannelId: this.defaultTriageChannelId,
      supportLogo: this.supportLogo,
      supportName: this.supportName,
      workingHours: this.workingHours,
      supportSteps: this.supportSteps,
      defaultTicketTypeId: this.defaultTicketType?.id ?? null,
      instructions: this.instructions,
      triageMessageTemplateId: this.triageMessageTemplateId,
      escalationSettings: this.escalationSettings,
      customTokens: this.customTokens,
      slackInboxConfig: this.slackInboxConfig,
      projectLevelPermissions: this.projectLevelPermissions,
      emojiActionsConfig: Object.keys(this.emojiActionsConfig).reduce((acc, action) => {
        this.emojiActionsConfig[action].forEach((emoji) => {
          acc[emoji] = action;
        });

        return acc;
      }, {}),
      approvePrefillAfterStatusId: this.approvePrefillAfterStatusId,
    };

    try {
      if (this.id) {
        await this.projectsService.updatePartial(this.id, updates);

        if (this.originalName !== this.name) {
          window.location.reload();
        } else {
          await this.msgSrvc.show('Project updated!');
        }
      } else {
        await this.projectsService.create(updates);

        await this.msgSrvc.show('Project added!');
      }

      this.dismiss(this.id ? 'update' : 'add');
    } catch (error) {
      await this.msgSrvc.showError(error);
    } finally {
      this.isLoading = false;
    }
  }

  async showDeleteConfirmation() {
    if (this.id === this.authService.tenant.defaultProjectId) {
      this.msgSrvc.show(
        `You can't delete the primary project. You can change the default project in your general settings`,
      );
      return;
    }
    const alert = await this.alertCtrl.create({
      header: 'Delete project?',
      subHeader: `This can't be undone. What would you like to do with existing customers and conversations?`,
      buttons: [
        {
          text: 'Cancel',
        },
        {
          text: 'Migrate data to other Project',
          handler: async () => {
            // show the searchable popover service
            this.searchablePopoverService.projects({
              event,
              showNoneOption: false,
              callback: (id: string, name: string) => {
                this.delete(id);
              },
            });
          },
        },
        {
          text: 'Delete data permanently',
          role: 'destructive',
          handler: async () => {
            this.showDeleteProjectConfirmation();
          },
        },
      ],
    });
    alert.present();
  }

  async delete(destinationProjectId?: string, deleteAll?: boolean) {
    const loader = await this.loadingCtrl.create({
      message: 'Deleting...',
      spinner: 'crescent',
    });

    loader.present();

    try {
      this.isLoading = true;
      await this.projectsService.delete(this.id, {
        deleteAll: deleteAll,
        destinationProjectId: destinationProjectId,
      });
      this.isLoading = false;

      this.msgSrvc.show('Project deleted');
      this.navCtrl.navigateRoot(routes.DASHBOARD);
      this.authService.storeSelectedProject(null);

      loader.dismiss();

      setTimeout(() => {
        window.location.reload();
      }, 500);

      this.dismiss('delete');
    } catch (e) {
      this.isLoading = false;
      loader.dismiss();
      this.msgSrvc.show(`Unable to delete project: ${e}`);
    }
  }

  async showDeleteProjectConfirmation() {
    const alert = await this.alertCtrl.create({
      header: 'Are you sure you want to delete?',
      subHeader: `We figured we'd give you one more chance before data is gone for good.`,
      buttons: [
        {
          text: 'Nevermind',
        },
        {
          text: 'Delete',
          role: 'destructive',
          handler: () => {
            this.delete(undefined, true);
          },
        },
      ],
    });
    alert.present();
  }

  dismiss(action?: 'delete' | 'update' | 'add') {
    this.modalCtrl.dismiss({ action });
  }

  async uploadSupportLogo() {
    const modal = await this.modalCtrl.create({
      component: ImageModalComponent,
      componentProps: {
        searchValue: '',
        helperText: '',
        aspectRatio: 1,
      },
    });
    await modal.present();

    const { data } = await modal.onWillDismiss();

    // if backdrop is clicked and no data passed
    if (!data || !data.uploadFile) {
      return;
    }

    this.uploadService
      .upload([data.uploadFile], `slack`, fileNameFromImageModalUpload(data.uploadFile))
      .then(() => {
        this.supportLogo = data.uploadFile.url;
      })
      .catch((err) => {
        this.msgSrvc.show(err.message);
      });
  }

  async manageWorkingHours() {
    const modal = await this.modalCtrl.create({
      component: WorkingHoursComponent,
      componentProps: {
        originalWorkingHours: this.workingHours ?? {},
        onSave: async (updates: any) => {
          const updatedProject = await this.projectsService.updatePartial(this.id, updates);
          this.workingHours = updatedProject.workingHours;
          this.workingHoursEnabled = updatedProject.workingHours?.enabled ?? false;
        },
      },
    });

    modal.present();
  }

  supportStepsDidChange(supportSteps: any[]) {
    this.supportSteps = supportSteps;
  }

  viewInInbox() {
    this.dismiss();

    this.authService.storeSelectedProject(this.project);
    this.navCtrl.navigateRoot(`${routes.DASHBOARD}/${routes.INBOX}/${INBOX_TYPES.ALL}`);
  }

  getIconForPriority(priorityKey: 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL'): string {
    const numericPriority = CONVERSATION_PRIORITY_VALUES[priorityKey];
    return getIconForConversationPriority(numericPriority);
  }

  async openPriorityEmojiPicker(priorityKey: 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL', event: any) {
    const popover = await this.popoverCtrl.create({
      component: EmojiPickerComponent,
      componentProps: {
        callback: (emoji: any) => {
          popover.dismiss();
          let emojiToStore: string;

          if (emoji.custom) {
            emojiToStore = `:${emoji.id}:`;
          } else if (emoji.colons) {
            emojiToStore = emoji.colons;
          } else if (emoji.id) {
            emojiToStore = `:${emoji.id}:`;
          }

          if (this.priorityLabelMapForm[priorityKey]) {
            this.priorityLabelMapForm[priorityKey].EMOJI = emojiToStore;
          }
        },
      },
      showBackdrop: false,
      cssClass: 'emoji-picker-popover',
      event,
    });

    popover.present();
  }

  deleteActionEmoji(action: string, i: number) {
    this.emojiActionsConfig[action]?.splice(i, 1);
  }

  selectPermissionTab(action: string, level: ProjectPermissionLevel | null) {
    this.selectedPermissionTabs[action] = level;

    // Update the project level permissions based on selection
    if (action === 'createConversation') {
      this.projectLevelPermissions.permissionLevelForEmojiConversationCreation = level;
    } else if (action === 'closeConversation') {
      this.projectLevelPermissions.permissionLevelForEmojiConversationClosing = level;
    } else if (action === 'inProgressConversation') {
      this.projectLevelPermissions.permissionLevelForEmojiConversationInProgress = level;
    } else if (action === 'onHoldConversation') {
      this.projectLevelPermissions.permissionLevelForEmojiConversationOnHold = level;
    }
  }

  getPermissionLabel(action: string): string {
    const currentValue = this.selectedPermissionTabs[action];
    const option = this.PERMISSION_LEVEL_OPTIONS.find((opt) => opt.value === currentValue);
    return option?.label || 'Agent Only';
  }

  showServiceAccounts() {
    this.navCtrl.navigateRoot(`/dashboard/settings/projects/${this.id}/service-accounts`);
  }

  async showPermissionOptions(action: string, event: Event) {
    const options: ActionDropdownType[] = this.PERMISSION_LEVEL_OPTIONS.map((option) => ({
      label: option.label,
      icon: this.selectedPermissionTabs[action] === option.value ? 'checkmark-circle' : 'ellipse-outline',
    }));

    const popover = await this.popoverCtrl.create({
      component: ActionDropdownComponent,
      componentProps: {
        options,
        callback: (index: number, label: string, id?: string, metadata?: any) => {
          const selectedLevel = this.PERMISSION_LEVEL_OPTIONS.find((opt) => opt.label === label);
          if (selectedLevel) {
            this.selectPermissionTab(action, selectedLevel.value);
          }
          popover.dismiss();
        },
      },
      event,
      cssClass: 'action-dropdown-popover',
    });

    await popover.present();
  }

  async openEmojiPicker(action: string, _event) {
    const popover = await this.popoverCtrl.create({
      component: EmojiPickerComponent,
      componentProps: {
        callback: (emoji: any) => {
          popover.dismiss();

          // Handle native and custom emojis
          const value = emoji.native ?? `:${emoji.id}:`;

          if (!this.emojiActionsConfig[action]) {
            this.emojiActionsConfig[action] = [];
          }

          this.emojiActionsConfig[action].push(value);
        },
      },
      showBackdrop: false,
      cssClass: 'emoji-picker-popover',
      event,
    });

    popover.present();
  }
}
